import React from 'react';
import { Shield, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { eventsData, categoryColors } from '../../../data/eventsData';

const getTrendInfo = (trend: string): { icon: React.ReactNode; color: string } => {
  const config: Record<string, { icon: React.ReactNode; color: string }> = {
    'ELEVATED': { icon: <TrendingUp size={12} />, color: 'text-data-red' },
    'STABLE': { icon: <Minus size={12} />, color: 'text-gray-400' },
    'DECLINING': { icon: <TrendingDown size={12} />, color: 'text-data-green' },
  };
  return config[trend] || { icon: <Minus size={12} />, color: 'text-gray-400' };
};

const getImpactStyle = (impact: string): string => {
  const styles: Record<string, string> = {
    'HIGH': 'bg-data-red text-white',
    'MEDIUM': 'bg-data-amber text-black',
    'LOW': 'bg-gray-600 text-white',
  };
  return styles[impact] || 'bg-gray-600 text-white';
};

interface RiskMonitorPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const RiskMonitorPanel: React.FC<RiskMonitorPanelProps> = ({ isMinimized, onMinimize }) => {
  const { risks, summary } = eventsData;

  // Calculate overall risk score
  const overallScore = risks.reduce((sum, r) => {
    const impactVal = r.impact === 'HIGH' ? 3 : r.impact === 'MEDIUM' ? 2 : 1;
    return sum + (r.probability * impactVal / 100);
  }, 0) / risks.length * 100;

  // Sort risks by composite score
  const sortedRisks = [...risks].sort((a, b) => {
    const scoreA = a.probability * (a.impact === 'HIGH' ? 1.5 : a.impact === 'MEDIUM' ? 1 : 0.5);
    const scoreB = b.probability * (b.impact === 'HIGH' ? 1.5 : b.impact === 'MEDIUM' ? 1 : 0.5);
    return scoreB - scoreA;
  });

  const decliningCount = risks.filter(r => r.trend === 'DECLINING').length;
  const elevatedCount = risks.filter(r => r.trend === 'ELEVATED').length;

  return (
    <Panel title="RISK MONITOR" subtitle="THREATS" status="static" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-3">
        {/* Hero - Overall Risk Level */}
        <div className="text-center py-4 px-4 bg-gradient-to-b from-data-green/10 to-transparent rounded-lg border border-data-green/20 mb-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield size={16} className="text-data-green" />
            <span className="text-xs text-data-green uppercase tracking-widest font-semibold">
              Risk Level
            </span>
          </div>
          <div className={`text-4xl font-mono font-black mb-1 ${
            summary.riskLevel === 'LOW' ? 'text-data-green' :
            summary.riskLevel === 'MODERATE' ? 'text-data-amber' : 'text-data-red'
          }`}>
            {summary.riskLevel}
          </div>
          <div className="text-xs text-gray-500">Score: {overallScore.toFixed(0)}/100</div>
        </div>

        {/* Trend Summary */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-data-green/30">
            <div className="text-2xl font-mono font-bold text-data-green">{decliningCount}</div>
            <div className="text-xs text-gray-500">Declining</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-white">{risks.length}</div>
            <div className="text-xs text-gray-500">Total Risks</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-data-red/30">
            <div className="text-2xl font-mono font-bold text-data-red">{elevatedCount}</div>
            <div className="text-xs text-gray-500">Elevated</div>
          </div>
        </div>

        {/* Risk Cards */}
        <div className="flex-1 overflow-auto custom-scrollbar space-y-2">
          {sortedRisks.slice(0, 5).map((risk, idx) => {
            const trendInfo = getTrendInfo(risk.trend);
            return (
              <div key={idx} className="bg-terminal-bg/40 rounded-lg p-3 border border-terminal-border/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-white">{risk.risk}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${getImpactStyle(risk.impact)}`}>
                    {risk.impact}
                  </span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-3">
                    <span style={{ color: categoryColors[risk.category] }} className="text-xs font-medium">
                      {risk.category}
                    </span>
                    <span className={`flex items-center gap-1 ${trendInfo.color}`}>
                      {trendInfo.icon}
                      <span className="text-xs">{risk.trend}</span>
                    </span>
                  </div>
                  <span className="text-xl font-mono font-black text-white">{risk.probability}%</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-auto pt-3 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default RiskMonitorPanel;
